// client/src/hooks/useChunkRecoveryNotifications.ts
// Hook to show notifications during chunk recovery

import { useEffect } from 'react';
import { useNotification } from '@/contexts/NotificationContext';

export const useChunkRecoveryNotifications = () => {
  const { showNotification } = useNotification();

  useEffect(() => {
    // Listen for chunk recovery events
    const handleChunkRecovery = (event: CustomEvent) => {
      const { source, success, duration } = event.detail;

      if (success) {
        showNotification(
          'info',
          'Updating App',
          `Recovering from error... Reloading in ${Math.ceil(duration / 1000)}s`
        );
      }
    };

    // Listen for cache loop detection
    const handleCacheLoop = (event: CustomEvent) => {
      showNotification(
        'info',
        'Clearing Cache',
        'App encountered repeated issues. Clearing cache and reloading...'
      );
    };

    // Listen for health status changes
    const handleHealthStatusChange = (event: CustomEvent) => {
      const { status, criticalChunks } = event.detail;

      if (status === 'critical') {
        showNotification(
          'error',
          'App Issues Detected',
          `${criticalChunks} critical issues detected. Please refresh the page.`
        );
      } else if (status === 'degraded') {
        showNotification(
          'info',
          'Performance Degraded',
          'App is experiencing performance issues. We are working on it.'
        );
      }
    };

    window.addEventListener('chunk-recovery', handleChunkRecovery as EventListener);
    window.addEventListener('cache-loop-detected', handleCacheLoop as EventListener);
    window.addEventListener('health-status-change', handleHealthStatusChange as EventListener);

    return () => {
      window.removeEventListener('chunk-recovery', handleChunkRecovery as EventListener);
      window.removeEventListener('cache-loop-detected', handleCacheLoop as EventListener);
      window.removeEventListener('health-status-change', handleHealthStatusChange as EventListener);
    };
  }, [showNotification]);
};

// Helper function to dispatch chunk recovery event
export const dispatchChunkRecoveryEvent = (source: string, success: boolean, duration: number) => {
  window.dispatchEvent(
    new CustomEvent('chunk-recovery', {
      detail: { source, success, duration },
    })
  );
};

// Helper function to dispatch cache loop event
export const dispatchCacheLoopEvent = () => {
  window.dispatchEvent(new CustomEvent('cache-loop-detected'));
};

// Helper function to dispatch health status change
export const dispatchHealthStatusChange = (status: string, criticalChunks: number) => {
  window.dispatchEvent(
    new CustomEvent('health-status-change', {
      detail: { status, criticalChunks },
    })
  );
};

